import { readFrameLight, type FrameLight } from "../effects/frame-light.ts";
import { asArray, asInteger, asNumber, asPositiveInteger, asRecord, asString } from "../document/validate.ts";

/**
 * The asset manifest (ADR-018): atlas pages and the rect of every frame, keyed by frame id
 * `<tilesetId>/<variant>/<tile>`. Clients derive frame ids from chunk data and look rects up here.
 */

/**
 * The layout this build writes. Schema 4 marks a transition animation `once` (ADR-058 decision 5); a
 * schema 3 manifest has no such mark and is still read, every animation in it a loop as the build that
 * wrote it drew them.
 */
export const ASSET_SCHEMA_VERSION = 4;

/**
 * The schema a manifest that holds a frame's light is written at (ADR-141's amendment of 2026-11-01, decision 3); a manifest
 * that holds none is written at `ASSET_SCHEMA_VERSION`, the lowest schema that can hold it.
 */
export const LIGHTS_ASSET_SCHEMA = 5;
const OLDEST_READ_SCHEMA_VERSION = 3;
const ONCE_SCHEMA = 4;
export const DEFAULT_VARIANT = "default";

/**
 * One atlas page.
 */
export interface AtlasPage {
  readonly file: string;
  readonly width: number;
  readonly height: number;
}

/**
 * A frame's rect on a page, excluding its extrusion. `base` is the row below the frame's last solid pixel
 * (1 to `h`; `h` for a frame with no transparent rows under its art, and for an empty frame): where the
 * art meets the ground, which props sort and collide by (ADR-040) rather than the frame's bottom edge;
 * soft pixels such as a drop shadow do not count unless the frame has nothing solid.
 * `empty` marks a frame with no opaque pixel at all (an unused slot of a tileset sheet, or a deliberate
 * transparent tile), so palettes can hide it while chunks can still stamp it. `ground` marks flat art
 * (paths, carpets, shadows) that draws under every character instead of sorting by row (ADR-040). `amongProps` is only ever
 * written beside `ground`: the frame then lies flat among props and sorts by the top edge of its art (ADR-040's amendment of
 * 2026-10-06, decision 2), an optional field of schema 4 that a reader which does not know it drops, leaving a plain
 * `ground` frame. `noFade` marks
 * a frame neither the occluder fade nor the cone of sight ever dims, the game's choice or the frame's own override made at the asset build
 * (`DaedalusGameAdapter.propNoFade`, `FootprintOverride.fades`; OdysseyTales: a building, whole or in parts), absent for
 * manifests written before it existed, when every frame fades. `art` is
 * the opaque bounds inside the frame: first opaque column and row and the opaque width, the bottom being
 * `base`; absent for an empty frame and for manifests written before it existed, when the whole frame
 * stands in for it. A frame is always drawn as one whole image at its own line (ADR-132 decision 1): a `depth` list of
 * pieces, which builds from before ADR-132 wrote on frames cut into parts, is read and dropped, well formed or not, as
 * the frames loop drops every field it does not know, so a world compiled before then still loads. `noFloor` marks a
 * frame that was painted and has no floor by intent (ADR-098 note 40); a frame nobody painted carries none. Nothing
 * writes it since ADR-131 replaced the painted floor and nothing draws by it; it is read-only legacy, kept so a
 * manifest that carries it still reads.
 */
export interface FrameRect {
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly empty?: true;
  readonly ground?: true;
  readonly amongProps?: true;
  readonly noFade?: true;
  readonly art?: FrameArt;
  readonly noFloor?: true;
}

/**
 * Least alpha that counts as solid art (ADR-040): a frame's `base` and `art` are measured from pixels at or
 * above it, footprints derive from them and the occluder fade counts them, while softer pixels (drop
 * shadows, glow) count for none of it.
 */
export const SOLID_ALPHA = 128;

/**
 * The opaque bounds of a frame, in frame pixels.
 */
export interface FrameArt {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

/**
 * A frame animation (ADR-041): the frames shown in order and how long each stays, in milliseconds. Keyed
 * by the frame that starts it, so a cell or prop holding that frame loops it. One marked `once` is a
 * transition animation (ADR-058 decision 5): nothing loops it, a cell or prop holding its first frame
 * shows that frame, and it plays only when a thing changes state.
 */
export interface FrameAnimation {
  readonly frames: readonly string[];
  readonly durations: readonly number[];
  readonly once?: true;
}

/**
 * The manifest.
 */
export interface AssetManifest {
  readonly schemaVersion: number;
  readonly pages: readonly AtlasPage[];
  readonly frames: { readonly [frameId: string]: FrameRect };
  readonly animations: { readonly [frameId: string]: FrameAnimation };
  /** Each packed frame's light, by frame id; absent or empty when no frame lights. */
  readonly lights?: { readonly [frameId: string]: FrameLight };
}

const INDENT = 2;
const SEPARATOR = "/";
const FRAME_ID = /^[^/]+\/[^/]+\/(0|[1-9][0-9]*)$/;

/**
 * Builds a frame id.
 *
 * @param tilesetId Tileset definition id.
 * @param variant Image variant name, or null for the default.
 * @param tile Local tile id.
 * @returns `<tilesetId>/<variant>/<tile>`.
 * @throws Error when the tileset id or variant contains `/`, or the tile is not a non-negative integer.
 */
export function frameId(tilesetId: string, variant: string | null, tile: number): string {
  const name = variant ?? DEFAULT_VARIANT;
  if (tilesetId === "" || tilesetId.includes(SEPARATOR)) {
    throw new Error(`tileset id ${JSON.stringify(tilesetId)} must be non-empty and contain no ${SEPARATOR}`);
  }
  if (name === "" || name.includes(SEPARATOR)) {
    throw new Error(`variant ${JSON.stringify(name)} must be non-empty and contain no ${SEPARATOR}`);
  }
  if (!Number.isInteger(tile) || tile < 0) {
    throw new Error(`tile ${tile} must be a non-negative integer`);
  }
  return `${tilesetId}${SEPARATOR}${name}${SEPARATOR}${tile}`;
}

/**
 * Parses a frame's opaque bounds, which must lie inside the frame and end at its base.
 *
 * @param value The `art` value.
 * @param path Path for messages.
 * @param w Frame width.
 * @param base Frame base row.
 * @returns The bounds.
 * @throws Error when a field is missing, not an integer, or the bounds leave the frame.
 */
function parseArt(value: unknown, path: string, w: number, base: number): FrameArt {
  const record = asRecord(value, path);
  const x = asInteger(record["x"], `${path}.x`);
  const y = asInteger(record["y"], `${path}.y`);
  const width = asPositiveInteger(record["w"], `${path}.w`);
  if (x < 0 || x + width > w) {
    throw new Error(`${path} spans columns ${x} to ${x + width - 1} of a ${w} wide frame`);
  }
  if (y < 0 || y >= base) {
    throw new Error(`${path}.y ${y} is not above the base row ${base}`);
  }
  return { x, y, w: width };
}

/**
 * Serialises a manifest with fixed key order and frames sorted by id (code-unit order).
 *
 * @param manifest The manifest.
 * @returns Pretty-printed JSON.
 */
export function serialiseAssetManifest(manifest: AssetManifest): string {
  const ids = Object.keys(manifest.frames).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const frames: Record<string, FrameRect> = {};
  for (const id of ids) {
    const rect = manifest.frames[id] as FrameRect;
    frames[id] = { page: rect.page, x: rect.x, y: rect.y, w: rect.w, h: rect.h, base: rect.base, ...(rect.empty === true ? { empty: true } : {}), ...(rect.ground === true ? { ground: true } : {}), ...(rect.ground === true && rect.amongProps === true ? { amongProps: true } : {}), ...(rect.noFade === true ? { noFade: true } : {}), ...(rect.art === undefined ? {} : { art: { x: rect.art.x, y: rect.art.y, w: rect.art.w } }), ...(rect.noFloor === true ? { noFloor: true } : {}) };
  }
  const animationIds = Object.keys(manifest.animations).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const animations: Record<string, FrameAnimation> = {};
  for (const id of animationIds) {
    const animation = manifest.animations[id] as FrameAnimation;
    animations[id] = { frames: [...animation.frames], durations: [...animation.durations], ...(animation.once === true ? { once: true } : {}) };
  }
  const lightIds = Object.keys(manifest.lights ?? {}).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const lights: Record<string, FrameLight> = {};
  for (const id of lightIds) {
    const light = (manifest.lights as Readonly<Record<string, FrameLight>>)[id] as FrameLight;
    lights[id] = { x: light.x, y: light.y, radius: light.radius, rgb: [light.rgb[0], light.rgb[1], light.rgb[2]], strength: light.strength, flicker: light.flicker, when: light.when, ...(light.unlit === undefined ? {} : { unlit: light.unlit }), ...(light.source === undefined ? {} : { source: light.source }) };
  }
  const schemaVersion = lightIds.length > 0 ? Math.max(manifest.schemaVersion, LIGHTS_ASSET_SCHEMA) : manifest.schemaVersion;
  return JSON.stringify({ schemaVersion, pages: manifest.pages.map((p) => ({ file: p.file, width: p.width, height: p.height })), frames, animations, ...(lightIds.length > 0 ? { lights } : {}) }, null, INDENT);
}

/**
 * An asset manifest of a schema this build does not read. `found` is the manifest's version and `newest` the
 * newest this build reads, so a reader can tell a manifest written for a newer build from one too old.
 */
export class AssetSchemaError extends Error {
  /**
   * @param found The manifest's schema version.
   * @param newest The newest version this build reads.
   */
  public constructor(
    public readonly found: number,
    public readonly newest: number,
  ) {
    super(`schemaVersion ${found} is not supported; this build reads ${OLDEST_READ_SCHEMA_VERSION} to ${newest}`);
    this.name = "AssetSchemaError";
  }
}

/**
 * Parses and validates manifest text.
 *
 * @param text JSON text.
 * @returns The manifest.
 * @throws SyntaxError when the text is not JSON; AssetSchemaError when the schema is not one this build reads;
 *   Error naming the offending path when a field is malformed, a frame id is not `<tileset>/<variant>/<tile>`, a frame lies on a
 *   missing page or outside its page, or a schema 3 manifest marks an animation `once`.
 */
export function deserialiseAssetManifest(text: string): AssetManifest {
  const record = asRecord(JSON.parse(text) as unknown, "assets");
  const schemaVersion = asNumber(record["schemaVersion"], "schemaVersion");
  if (!Number.isInteger(schemaVersion) || schemaVersion < OLDEST_READ_SCHEMA_VERSION || schemaVersion > LIGHTS_ASSET_SCHEMA) {
    throw new AssetSchemaError(schemaVersion, LIGHTS_ASSET_SCHEMA);
  }
  const pages = asArray(record["pages"], "pages").map((item, i) => {
    const page = asRecord(item, `pages[${i}]`);
    return { file: asString(page["file"], `pages[${i}].file`), width: asPositiveInteger(page["width"], `pages[${i}].width`), height: asPositiveInteger(page["height"], `pages[${i}].height`) };
  });
  const framesRecord = asRecord(record["frames"], "frames");
  const frames: Record<string, FrameRect> = {};
  for (const id of Object.keys(framesRecord)) {
    if (!FRAME_ID.test(id)) {
      throw new Error(`frames.${id} is not a frame id of the form tileset/variant/tile`);
    }
    const path = `frames.${id}`;
    const rect = asRecord(framesRecord[id], path);
    const page = asInteger(rect["page"], `${path}.page`);
    const atlas = pages[page];
    if (atlas === undefined) {
      throw new Error(`${path} is on page ${page}, but the manifest has ${pages.length} pages`);
    }
    const x = asInteger(rect["x"], `${path}.x`);
    const y = asInteger(rect["y"], `${path}.y`);
    const w = asPositiveInteger(rect["w"], `${path}.w`);
    const h = asPositiveInteger(rect["h"], `${path}.h`);
    if (x < 0 || y < 0 || x + w > atlas.width || y + h > atlas.height) {
      throw new Error(`${path} lies outside its ${atlas.width}x${atlas.height} page`);
    }
    const base = asPositiveInteger(rect["base"], `${path}.base`);
    if (base > h) {
      throw new Error(`${path}.base ${base} is below the frame's ${h} rows`);
    }
    const empty = rect["empty"];
    if (empty !== undefined && empty !== true) {
      throw new Error(`${path}.empty must be true when present`);
    }
    const ground = rect["ground"];
    if (ground !== undefined && ground !== true) {
      throw new Error(`${path}.ground must be true when present`);
    }
    const amongProps = rect["amongProps"];
    if (amongProps !== undefined && amongProps !== true) {
      throw new Error(`${path}.amongProps must be true when present`);
    }
    if (amongProps === true && ground !== true) {
      throw new Error(`${path}.amongProps needs ground: true beside it`);
    }
    const noFade = rect["noFade"];
    if (noFade !== undefined && noFade !== true) {
      throw new Error(`${path}.noFade must be true when present`);
    }
    const art = rect["art"] === undefined ? undefined : parseArt(rect["art"], `${path}.art`, w, base);
    const noFloor = rect["noFloor"];
    if (noFloor !== undefined && noFloor !== true) {
      throw new Error(`${path}.noFloor must be true when present`);
    }
    frames[id] = { page, x, y, w, h, base, ...(empty === true ? { empty: true } : {}), ...(ground === true ? { ground: true } : {}), ...(amongProps === true ? { amongProps: true } : {}), ...(noFade === true ? { noFade: true } : {}), ...(art === undefined ? {} : { art }), ...(noFloor === true ? { noFloor: true } : {}) };
  }
  const animations: Record<string, FrameAnimation> = {};
  const animationsRecord = record["animations"] === undefined ? {} : asRecord(record["animations"], "animations");
  for (const id of Object.keys(animationsRecord)) {
    const path = `animations.${id}`;
    if (frames[id] === undefined) {
      throw new Error(`${path} starts at a frame the manifest lacks`);
    }
    const animation = asRecord(animationsRecord[id], path);
    const ids = asArray(animation["frames"], `${path}.frames`).map((item, i) => asString(item, `${path}.frames[${i}]`));
    const durations = asArray(animation["durations"], `${path}.durations`).map((item, i) => asPositiveInteger(item, `${path}.durations[${i}]`));
    if (ids.length === 0 || ids.length !== durations.length) {
      throw new Error(`${path}.durations must hold one positive duration per frame`);
    }
    for (const frame of ids) {
      if (frames[frame] === undefined) {
        throw new Error(`${path} names frame ${frame}, which the manifest lacks`);
      }
    }
    const once = animation["once"];
    if (once !== undefined && once !== true) {
      throw new Error(`${path}.once must be true when present`);
    }
    if (once === true && schemaVersion < ONCE_SCHEMA) {
      throw new Error(`${path} is marked once, which only schema ${ONCE_SCHEMA} and later carry, in a schema ${schemaVersion} manifest`);
    }
    animations[id] = { frames: ids, durations, ...(once === true ? { once: true } : {}) };
  }
  const lightsRecord = record["lights"] === undefined ? undefined : asRecord(record["lights"], "lights");
  if (lightsRecord === undefined) {
    return { schemaVersion, pages, frames, animations };
  }
  if (schemaVersion < LIGHTS_ASSET_SCHEMA) {
    throw new Error(`lights are carried by schema ${LIGHTS_ASSET_SCHEMA}, not in a schema ${schemaVersion} manifest`);
  }
  const lights: Record<string, FrameLight> = {};
  for (const id of Object.keys(lightsRecord)) {
    const path = `lights.${id}`;
    if (frames[id] === undefined) {
      throw new Error(`${path} lights a frame the manifest lacks`);
    }
    const light = readFrameLight(lightsRecord[id], path);
    if (light.unlit !== undefined && frames[light.unlit] === undefined) {
      throw new Error(`${path}.unlit names frame ${light.unlit}, which the manifest lacks`);
    }
    lights[id] = light;
  }
  return { schemaVersion, pages, frames, animations, lights };
}
